import * as React from 'react';
import { change } from 'redux-form';
import { path } from 'ramda';

export const useNullifyDependentFields = ({ dispatch, formValues }) => {
  const nullify = dkey => dispatch(change('msBackground', dkey, null));

  const {
    familyPrevalence,
    reproductiveStatus,
    usingContraceptive,
    currentStatus,
    retirementType,
    occupation,
    statusAsEmployee,
    workAbilityScore,
  } = formValues;

  const dependentFields = {
    familyPrevalence: ['familyPrevalenceDetail'],
    reproductiveStatus: ['pregnancyDueDate'],
    usingContraceptive: ['contraceptives'],
    currentStatus: [
      'partTimeWorkedHours',
      'sickLeaveDaysPassed',
      'unemployedDaysPassed',
      'retirementType',
      'occupation',
      'workAbilityScore',
      'selfAssessmentAbilityToWorkIn2Years',
    ],
    retirementType: ['partTimePensionWorkedHours', 'occupationBeforePension', 'workAbilityScore'],
    occupation: ['statusAsEmployee'],
    statusAsEmployee: ['otherEmployeeDetail'],
    'workAbilityScore.workAbilityComparedToLifetimeBest': [
      'workAbilityScore.reductionReason',
      'workAbilityScore.reductionType',
    ],
  };

  React.useEffect(() => {
    Object.keys(dependentFields).forEach(key => {
      const value = path(key.split('.'), formValues);
      if (!value) return;

      dependentFields[key].forEach(dkey => {
        const dvalue = path(dkey.split('.'), formValues);
        if (!dvalue) return;

        switch (dkey) {
          /** familyPrevalence */
          case 'familyPrevalenceDetail': {
            if (value !== 'yes') nullify(dkey);
            break;
          }
          /** reproductiveStatus */
          case 'pregnancyDueDate': {
            if (value !== 'pregnant') nullify(dkey);
            break;
          }
          /** usingContraceptive */
          case 'contraceptives': {
            if (value !== 'yes') nullify(dkey);
            break;
          }
          /** currentStatus */
          case 'partTimeWorkedHours': {
            if (!value.includes('partTime')) nullify(dkey);
            break;
          }
          case 'sickLeaveDaysPassed': {
            if (!value.includes('sickLeave')) nullify(dkey);
            break;
          }
          case 'unemployedDaysPassed': {
            if (!value.includes('unemployedOrLaidOff')) nullify(dkey);
            break;
          }
          case 'retirementType': {
            if (!value.includes('retired')) nullify(dkey);
            break;
          }
          case 'occupation':
          case 'workAbilityScore': {
            if (Array.isArray(value)) {
              if (!value.some(v => ['fullTime', 'partTime', 'unemployedOrLaidOff', 'sickLeave'].includes(v))) {
                if (
                  value.includes('retired') &&
                  ['unemploymentPension', 'partTimePension'].includes(formValues.retirementType)
                ) {
                  break;
                }
                nullify(dkey);
              }
            } else if (dkey === 'workAbilityScore' && value !== 'partTimePension') nullify(dkey);
            break;
          }
          case 'selfAssessmentAbilityToWorkIn2Years': {
            if (!value.some(v => ['fullTime', 'partTime'].includes(v))) nullify(dkey);
            break;
          }
          /** retirementType */
          case 'partTimePensionWorkedHours': {
            if (value !== 'partTimePension') nullify(dkey);
            break;
          }
          case 'occupationBeforePension': {
            if (!['unemploymentPension', 'oldAgePension'].includes(value)) nullify(dkey);
            break;
          }
          // case 'workAbilityScore': {
          // --> handled above with 'occupation'
          // }
          /** occupation */
          case 'statusAsEmployee': {
            if (value !== 'employee') nullify(dkey);
            break;
          }
          /** statusAsEmployee */
          case 'otherEmployeeDetail': {
            if (value !== 'otherEmployee') nullify(dkey);
            break;
          }
          /** workAbilityScore.workAbilityComparedToLifetimeBest */
          case 'workAbilityScore.reductionReason':
          case 'workAbilityScore.reductionType': {
            if (!value || value > 7)
              dispatch(change('msBackground', 'workAbilityScore', { workAbilityComparedToLifetimeBest: value }));
            break;
          }
          /** default */
          default: {
            nullify(dkey);
            break;
          }
        }
      });
    });
  }, [
    familyPrevalence,
    reproductiveStatus,
    usingContraceptive,
    currentStatus,
    retirementType,
    occupation,
    statusAsEmployee,
    workAbilityScore,
  ]);
};
